/* eslint-disable react/jsx-boolean-value */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable no-alert */
import React, { useState, useEffect, useMemo } from "react";
import "firebase/database";
import {
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Grid,
  Typography,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import Select from "react-select";
import { useHistory } from "react-router-dom";
import { useCompany } from "../../contexts/CompanyContext";
import { supabase } from "../../supabase";
import UpsertTrainingModule from "./UpsertTrainingModule";

/**
 * The TrainingModules component manages the display and filtering of training modules.
 * It provides options to create, edit, and view modules, while applying filters
 * based on module status, product type, and supplier. The component fetches data
 * from a Supabase database and organizes it for user interaction through dialogs
 * and forms.
 *
 * State Variables:
 * - `modules`: Stores the list of training modules fetched from the database.
 * - `loadingPage`: Indicates whether the component is in a loading state.
 * - `openDialog`: Controls the visibility of the module creation/edit dialog.
 * - `moduleId`: Holds the ID of the module being edited.
 * - `active`: Filter state for active/inactive modules.
 * - `productType`, `supplier`: Filter states for product types and suppliers.
 * - `formattedProductTypes`, `availableSuppliers`: Formatted arrays for product types and suppliers based on current data.
 * - `modulesDisplay`: Stores the filtered and display-ready list of modules.
 * - `trainingTypes`: Stores the list of training types.
 * - `statusMessage`: Controls the visibility and content of the status message snackbar.
 *
 * Effects:
 * - Formats product types and supplier data for UI components.
 * - Fetches modules and training types from the Supabase database on component mount.
 * - Applies filters to modules based on the selected criteria and updates the display.
 *
 * Functions:
 * - `applyFilters`: Filters modules based on status, product type, and supplier.
 * - `fetchModules`, `fetchTrainingTypes`: Asynchronously fetch data from Supabase.
 * - `handleDialogOpen`, `handleDialogClose`: Manage the opening and closing of the module dialog.
 * - `handleOpenMessage`, `handleCloseMessage`: Manage the opening and closing of the status message snackbar.
 */
export default function TrainingModules() {
  // Default state moduleState

  const history = useHistory();
  const [modules, setModules] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [moduleId, setModuleId] = useState(undefined);
  // Filter states
  const [active, setActive] = useState(true);
  const [productType, setProductType] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const { productTypes, activeSuppliers } = useCompany();
  const [formattedProductTypes, setFormattedProductTypes] = useState([]);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [modulesDisplay, setModulesDisplay] = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [statusMessage, setStatusMessage] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const sortedModules = useMemo(() => {
    return modulesDisplay.sort((a, b) => {
      if (a.module_category && b.module_category) {
        return a.module_category.localeCompare(b.module_category);
      }
      if (a.module_category) {
        return -1; // Place items with a category before those without
      }
      if (b.module_category) {
        return 1; // Place items with a category after those without
      }
      return 0; // Leave the order unchanged if both items lack a category
    });
  }, [modulesDisplay]);

  useEffect(() => {
    // Format the product types array for active product types and update the state
    const formattedProductTypesArray = productTypes.map(type => ({
      label: type.name,
      value: type.id,
    }));

    setFormattedProductTypes(formattedProductTypesArray);
  }, [productTypes]);

  useEffect(() => {
    // Map active suppliers into correct format
    const supplierMap = activeSuppliers.map(sup => ({
      label: sup.supplier_name,
      value: sup.id,
    }));

    setAvailableSuppliers(supplierMap);
  }, [activeSuppliers]);

  useEffect(() => {
    // Fetch all training modules and update the state
    fetchModules();
    fetchTrainingTypes();
  }, []);

  useEffect(() => {
    applyFilters(productType, supplier, active);
  }, [active, productType, supplier, modules]);

  /**
   * Applies filters to the list of modules, updating the `modulesDisplay` state.
   * @param {{value: string}} [productTypeVal] - The selected product type value.
   * @param {{value: string}} [supplierVal] - The selected supplier value.
   * @param {boolean} [activeVal=true] - The value to filter by for the `active` field.
   * @returns {void} - Updates the `modulesDisplay` state.
   */
  const applyFilters = (productTypeVal, supplierVal, activeVal = true) => {
    let tempModules = [...modules];

    const filters = [
      { field: "active", value: activeVal },
      {
        field: "training_refs_product_type_index",
        value: productTypeVal?.value,
      },
      { field: "training_supplier_index", value: supplierVal?.value },
    ];

    filters.forEach(filter => {
      if (filter.value !== undefined && filter.value !== "All") {
        tempModules = tempModules.filter(obj => {
          if (filter.field === "active") {
            return obj.active === activeVal;
          }

          const fieldValue = obj[filter.field];
          if (!fieldValue) return false; // Ignore undefined or null fields

          // Check if fieldValue is an array, handle includes logic
          return Array.isArray(fieldValue)
            ? fieldValue.includes(filter.value)
            : false;
        });
      }
    });

    // Update the display state once after filtering
    setModulesDisplay(tempModules);
  };

  /**
   * Fetches training modules from the "training_master" table in Supabase,
   * filtering out deleted modules and organizing product types and suppliers
   * for each module. Updates the `modules` state with the fetched data, and
   * applies filters based on product type, supplier, and active status.
   *
   * @returns {Promise<void>} - Resolves when the modules are fetched and state is updated.
   * @throws Logs an error if the data fetching fails.
   */
  const fetchModules = async () => {
    setLoadingPage(true); // Start loading

    try {
      // Query the Supabase table
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("training_master")
        .select(
          "*, training_refs_product_type_index(refs_product_type_id, is_active), training_supplier_index(suppliers_master_id, is_active)"
        )
        .eq("is_deleted", false)
        .order("module_name");

      // Handle error
      if (error) {
        console.error("Error fetching modules:", error);
        return; // Stop execution if there's an error
      }

      // Set the data
      const dataMap = data?.map(module => {
        const { training_refs_product_type_index, training_supplier_index } =
          module;

        const selectedProductTypes = training_refs_product_type_index
          .filter(({ is_active }) => is_active)
          .map(({ refs_product_type_id }) => refs_product_type_id);

        const selectedSuppliers = training_supplier_index
          .filter(({ is_active }) => is_active)
          .map(({ suppliers_master_id }) => suppliers_master_id);

        return {
          ...module,
          training_refs_product_type_index: selectedProductTypes,
          training_supplier_index: selectedSuppliers,
        };
      });

      setModules(dataMap);
      applyFilters(productType, supplier, active);
      setLoadingPage(false);
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      // End loading regardless of success or failure
      setLoadingPage(false);
    }
  };

  /**
   * Fetches all training types from the "refs_training_type" table in Supabase.
   *
   * The function retrieves all available training types and updates the
   * `trainingTypes` state with the fetched data.
   *
   * @returns {Promise<void>} - Resolves when the training types are fetched and state is updated.
   * @throws Will throw an error message if the data fetching fails.
   */
  const fetchTrainingTypes = async () => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("refs_user_segment_type")
      .select("*")
      .order("id", { ascending: true });

    if (error) {
      console.error(error);
      throw error.message;
    } else {
      setTrainingTypes(data);
    }
  };

  // Open the dialog for editing or creating a module
  const handleDialogOpen = module => {
    if (module) {
      setModuleId(module.id);
    }
    setOpenDialog(true);
  };

  // Close the dialog for editing or creating a module
  const handleDialogClose = () => {
    setOpenDialog(false);
    setModuleId(undefined);
    fetchModules();
  };

  const handleOpenMessage = (message, severity = "info") => {
    setStatusMessage({ open: true, message, severity });
  };

  const handleCloseMessage = () => {
    setStatusMessage({ open: false, message: "" });
  };

  return (
    <div>
      <h1>Training Modules</h1>
      {loadingPage && <CircularProgress />}
      {/* Filters */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          <h3>Filters</h3>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            fullWidth
            placeholder="Status"
            name="active"
            options={[
              { label: "Active", value: true },
              { label: "Inactive", value: false },
            ]}
            defaultValue={{ label: "Active", value: true }}
            onChange={e => {
              setActive(e.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            fullWidth
            placeholder="Product Type"
            name="productType"
            options={[
              {
                label: "All",
                value: "All",
              },
              ...formattedProductTypes,
            ]}
            value={productType}
            onChange={e => {
              setProductType(e);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Select
            fullWidth
            placeholder="Supplier"
            name="supplier"
            options={[
              {
                label: "All",
                value: "All",
              },
              ...availableSuppliers,
            ]}
            value={supplier}
            onChange={e => {
              setSupplier(e);
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          <Button
            onClick={() => {
              handleDialogOpen();
            }}
            variant="outlined"
            color="primary"
          >
            Create New Module
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          <h3>Module List</h3>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        {sortedModules.map(module => (
          <Grid item xs={12} sm={7} key={module.id}>
            <div>
              <List>
                <ListItem
                  style={{
                    marginBottom: 10,
                    borderBottom: "solid 1px #d2d2d2",
                  }}
                  key={module.id}
                >
                  <ListItemText
                    primary={module.module_name}
                    secondary={
                      module.module_category ? (
                        <Typography variant="overline">
                          {module.module_category}
                        </Typography>
                      ) : (
                        "No Category."
                      )
                    }
                  />
                  <Button
                    onClick={() => {
                      history.push(
                        `/training-content/${module.id}/${module.module_name}`
                      );
                    }}
                  >
                    See Content
                  </Button>
                  <Button onClick={() => handleDialogOpen(module)}>Edit</Button>
                  <Button
                    onClick={() => {
                      history.push(
                        `/training-marks/${module.id}/${module.module_name}`
                      );
                    }}
                  >
                    View Marks
                  </Button>
                </ListItem>
              </List>
            </div>
          </Grid>
        ))}
        {sortedModules.length === 0 && loadingPage === false && (
          <Grid item xs={12} sm={6}>
            <Typography>No Modules Found.</Typography>
          </Grid>
        )}
      </Grid>
      <UpsertTrainingModule
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        moduleId={moduleId}
        trainingTypes={trainingTypes}
        handleOpenMessage={handleOpenMessage}
      />
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={statusMessage.open}
        onClose={handleCloseMessage}
        autoHideDuration={3000}
      >
        <Alert severity={statusMessage.severity}>{statusMessage.message}</Alert>
      </Snackbar>
    </div>
  );
}
